import React, { useState } from 'react';
import { Calendar, Users, Search, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import apiClient from '../lib/apiClient';

const AvailabilityChecker = ({ room, onProceedToBook, onClose }) => {
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(1);
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState(null); // { available: bool, availableUnits: number }
  const [error, setError] = useState('');

  const maxGuests = room?.occupancy || 6;

  const checkAvailability = async () => {
    if (!checkIn || !checkOut) { alert('Please select both check-in and check-out dates'); return; }
    if (new Date(checkOut) <= new Date(checkIn)) { alert('Check-out date must be after check-in date'); return; }

    setIsChecking(true);
    setError('');
    setResult(null);
    try {
      const data = await apiClient.post('/api/Search', {
        numberOfGuests: guests,
        checkInDate: checkIn,
        checkOutDate: checkOut,
      });

      const allRooms = data?.availableRooms || [];
      const matched = allRooms.find(r => r.roomTypeId === room?.roomTypeId);

      if (matched && matched.availableUnits > 0) {
        setResult({ available: true, availableUnits: matched.availableUnits, price: matched.price });
      } else {
        setResult({ available: false, availableUnits: 0 });
      }
    } catch (err) {
      setError(err.message || 'Could not check availability.');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <Card className="w-full max-w-xs sm:max-w-md bg-white rounded-2xl shadow-2xl max-h-[95vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Check Availability</h2>
              {room?.name && <p className="text-blue-100 text-sm mt-0.5">{room.name}</p>}
            </div>
            <button onClick={onClose} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-lg">×</button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {room?.occupancy && (
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg px-3 py-2">
              <Users size={14} className="text-blue-500" />
              <span>Max capacity: <strong>{room.occupancy} guest{room.occupancy !== 1 ? 's' : ''}</strong> per room</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Calendar size={14} /> Check-in
              </label>
              <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Calendar size={14} /> Check-out
              </label>
              <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)}
                min={checkIn || new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Users size={14} /> Guests
              </label>
              <select value={guests} onChange={e => setGuests(+e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                {Array.from({ length: maxGuests }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </div>
          </div>

          <Button onClick={checkAvailability} disabled={isChecking || !checkIn || !checkOut} className="w-full">
            {isChecking
              ? <><Search className="animate-spin mr-2" size={16} />Checking...</>
              : <><Search className="mr-2" size={16} />Check Availability</>}
          </Button>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {result !== null && (
            <div className={`p-4 rounded-xl ${result.available ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-3">
                {result.available
                  ? <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={22} />
                  : <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" size={22} />}
                <div>
                  <h3 className={`font-semibold ${result.available ? 'text-green-800' : 'text-red-800'}`}>
                    {result.available
                      ? `${result.availableUnits} unit${result.availableUnits !== 1 ? 's' : ''} available`
                      : 'Not available for these dates'}
                  </h3>
                  {result.available && (
                    <p className="text-sm text-green-700 mt-0.5">৳{result.price.toLocaleString()} per night</p>
                  )}
                </div>
              </div>

              {result.available && (
                <div className="mt-4 flex gap-2">
                  <Button
                    onClick={() => onProceedToBook({ checkIn, checkOut, guests })}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-sm"
                  >
                    Proceed to Booking
                  </Button>
                  <Button onClick={() => setResult(null)} variant="outline" className="text-sm">Reset</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AvailabilityChecker;
